import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Search, X, Package, ChevronRight, Send, ArrowRight, FileDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function groupProducts(products, language) {
  const lang = language || 'nl';
  const groups = {};
  products.forEach((product) => {
    const name = product[`name_${lang}`] || product.name_nl || '';
    let base = name.replace(/\s*L=[\d,]+M$/i, '').replace(/\s*[\d,]+x[\d,]+(mm)?$/i, '').replace(/\s*Ø[\d,]+mm$/i, '').replace(/\s*Size\s*\d+$/i, '').replace(/\s*(2xPack|set\s.*)$/i, '').trim();
    const variant = name.replace(base, '').trim();
    if (!groups[base]) {
      groups[base] = { base, products: [], variants: [], category_id: product.category_id, image_url: product.image_url, firstProduct: product };
    }
    groups[base].products.push(product);
    if (variant) groups[base].variants.push({ label: variant, product });
  });
  return Object.values(groups);
}

export const ProductsPage = () => {
  const { t, language, getLocalizedField } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(`${API}/products`), axios.get(`${API}/categories`)])
      .then(([pRes, cRes]) => { setProducts(pRes.data); setCategories(cRes.data.sort((a, b) => (a.order || 0) - (b.order || 0))); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { const c = searchParams.get('category'); if (c) setSelectedCategory(c); }, [searchParams]);

  const handleCategoryChange = (val) => {
    setSelectedCategory(val); setExpandedProduct(null);
    if (val === 'all') searchParams.delete('category'); else searchParams.set('category', val);
    setSearchParams(searchParams); setSearchQuery('');
  };

  const getCount = (catId) => products.filter(p => p.category_id === catId).length;

  const filtered = useMemo(() => {
    let list = selectedCategory === 'all' ? products : products.filter(p => p.category_id === selectedCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => (p[`name_${language}`] || p.name_nl || '').toLowerCase().includes(q));
    }
    return list;
  }, [products, selectedCategory, searchQuery, language]);

  const grouped = useMemo(() => groupProducts(filtered, language), [filtered, language]);
  const selectedCatObj = categories.find(c => c.id === selectedCategory);

  const tx = {
    nl: { title: 'Assortiment', all: 'Alle Producten', categories: 'CATEGORIEEN', products: 'producten', searchPlaceholder: 'Zoek producten...', noResults: 'Geen producten gevonden', clearSearch: 'Wis zoekopdracht', variants: 'AFMETINGEN', viewDetails: 'Details', specs: 'Specificaties', requestTitle: 'Offerte Aanvragen', requestName: 'Naam', requestCompany: 'Bedrijf', requestBtw: 'BTW Nummer', requestEmail: 'E-mail', requestPhone: 'Telefoon', requestCountry: 'Land', requestQty: 'Aantal', requestOrderType: 'Bestelvorm', requestOrderPallet: 'Palet', requestOrderTruck: 'Vrachtwagen', requestMsg: 'Bericht', requestSubmit: 'Verstuur', requestSuccess: 'Uw aanvraag is verzonden.', recommended: 'Gerelateerde Producten', certificate: 'Certificaat', downloadCert: 'Download certificaat' },
    en: { title: 'Product Range', all: 'All Products', categories: 'CATEGORIES', products: 'products', searchPlaceholder: 'Search products...', noResults: 'No products found', clearSearch: 'Clear search', variants: 'DIMENSIONS', viewDetails: 'Details', specs: 'Specifications', requestTitle: 'Request Quote', requestName: 'Name', requestCompany: 'Company', requestBtw: 'VAT Number', requestEmail: 'Email', requestPhone: 'Phone', requestCountry: 'Country', requestQty: 'Quantity', requestOrderType: 'Order type', requestOrderPallet: 'Pallet', requestOrderTruck: 'Truck', requestMsg: 'Message', requestSubmit: 'Submit', requestSuccess: 'Your request has been sent.', recommended: 'Related Products', certificate: 'Certificate', downloadCert: 'Download certificate' },
    fr: { title: 'Gamme de Produits', all: 'Tous les Produits', categories: 'CATEGORIES', products: 'produits', searchPlaceholder: 'Rechercher...', noResults: 'Aucun produit trouve', clearSearch: 'Effacer', variants: 'DIMENSIONS', viewDetails: 'Details', specs: 'Specifications', requestTitle: 'Demander un Devis', requestName: 'Nom', requestCompany: 'Entreprise', requestBtw: 'Numero TVA', requestEmail: 'E-mail', requestPhone: 'Telephone', requestCountry: 'Pays', requestQty: 'Quantite', requestOrderType: 'Type de commande', requestOrderPallet: 'Palette', requestOrderTruck: 'Camion', requestMsg: 'Message', requestSubmit: 'Envoyer', requestSuccess: 'Votre demande a ete envoyee.', recommended: 'Produits Associes', certificate: 'Certificat', downloadCert: 'Telecharger certificat' },
    de: { title: 'Produktsortiment', all: 'Alle Produkte', categories: 'KATEGORIEN', products: 'Produkte', searchPlaceholder: 'Suchen...', noResults: 'Keine Produkte gefunden', clearSearch: 'Loschen', variants: 'ABMESSUNGEN', viewDetails: 'Details', specs: 'Spezifikationen', requestTitle: 'Angebot Anfordern', requestName: 'Name', requestCompany: 'Unternehmen', requestBtw: 'USt-IdNr.', requestEmail: 'E-Mail', requestPhone: 'Telefon', requestCountry: 'Land', requestQty: 'Menge', requestOrderType: 'Bestellart', requestOrderPallet: 'Palette', requestOrderTruck: 'LKW', requestMsg: 'Nachricht', requestSubmit: 'Absenden', requestSuccess: 'Ihre Anfrage wurde gesendet.', recommended: 'Verwandte Produkte', certificate: 'Zertifikat', downloadCert: 'Zertifikat herunterladen' },
  }[language] || { title: 'Assortiment', all: 'Alle Producten', categories: 'CATEGORIEEN', products: 'producten', searchPlaceholder: 'Zoek producten...', noResults: 'Geen producten gevonden', clearSearch: 'Wis', variants: 'AFMETINGEN', viewDetails: 'Details', specs: 'Specificaties', requestTitle: 'Offerte Aanvragen', requestName: 'Naam', requestCompany: 'Bedrijf', requestEmail: 'E-mail', requestPhone: 'Telefoon', requestQty: 'Aantal', requestMsg: 'Bericht', requestSubmit: 'Verstuur', requestSuccess: 'Verzonden.', recommended: 'Gerelateerd' };

  return (
    <div data-testid="products-page">
      {/* Header */}
      <section className="bg-[#0F172A] pt-28 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-4">{tx.title}</h1>
          <div className="max-w-sm relative" data-testid="search-bar">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={tx.searchPlaceholder}
              className="w-full pl-10 pr-9 py-2.5 bg-white/5 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-white/25 transition-colors" data-testid="search-input" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" data-testid="search-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Mobile select */}
          <div className="lg:hidden mb-5">
            <select value={selectedCategory} onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 text-sm bg-white" data-testid="mobile-category-select">
              <option value="all">{tx.all} ({products.length})</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{getLocalizedField(cat, 'name')} ({getCount(cat.id)})</option>)}
            </select>
          </div>

          <div className="lg:flex lg:gap-10">
            {/* Sidebar */}
            <aside className="hidden lg:block w-56 flex-shrink-0" data-testid="category-sidebar">
              <div className="sticky top-24">
                <h3 className="text-[10px] font-bold text-slate-400 tracking-[0.15em] uppercase mb-4">{tx.categories}</h3>
                <button onClick={() => handleCategoryChange('all')} data-testid="sidebar-all"
                  className={`w-full text-left py-2 text-[13px] border-b border-gray-100 transition-colors ${selectedCategory === 'all' ? 'text-[#BE1622] font-semibold' : 'text-slate-600 hover:text-slate-900'}`}>
                  {tx.all}
                </button>
                {categories.map((cat, i) => (
                  <button key={cat.id} onClick={() => handleCategoryChange(cat.id)} data-testid={`sidebar-cat-${i}`}
                    className={`w-full text-left py-2 text-[13px] border-b border-gray-100 transition-colors ${selectedCategory === cat.id ? 'text-[#BE1622] font-semibold' : 'text-slate-600 hover:text-slate-900'}`}>
                    {getLocalizedField(cat, 'name')}
                  </button>
                ))}
              </div>
            </aside>

            {/* Products */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-bold text-slate-900 tracking-tight">{selectedCatObj ? getLocalizedField(selectedCatObj, 'name') : tx.all}</h2>
                <span className="text-xs text-slate-400">{grouped.length} {tx.products}</span>
              </div>

              {loading ? (
                <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-[#BE1622] border-t-transparent rounded-full animate-spin" /></div>
              ) : grouped.length === 0 ? (
                <div className="text-center py-20">
                  <Package className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-2">{tx.noResults}</p>
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="text-[#BE1622] text-xs font-medium hover:underline">{tx.clearSearch}</button>}
                </div>
              ) : selectedCategory === 'all' && !searchQuery ? (
                /* Category overview - clean corporate grid */
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                  {categories.map((cat, i) => (
                    <button key={cat.id} onClick={() => handleCategoryChange(cat.id)} className="group text-left border border-gray-200 hover:border-[#BE1622]/30 transition-all" data-testid={`cat-overview-${i}`}>
                      <div className="aspect-[4/3] bg-[#F8FAFC] p-5 flex items-center justify-center">
                        <img src={cat.image_url || ''} alt="" className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="p-4 border-t border-gray-100">
                        <h3 className="text-xs font-semibold text-slate-900 group-hover:text-[#BE1622] transition-colors leading-tight">{getLocalizedField(cat, 'name')}</h3>
                        <span className="text-[10px] text-slate-400 mt-1 block">{getCount(cat.id)} {tx.products}</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                /* Product grid */
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                  {grouped.map((group, gi) => (
                    <ProductCard key={gi} group={group} index={gi} language={language} getLocalizedField={getLocalizedField} tx={tx}
                      expanded={expandedProduct === gi} onToggle={() => setExpandedProduct(expandedProduct === gi ? null : gi)} navigate={navigate} allGroups={grouped} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* Product Card */
const ProductCard = ({ group, index, language, tx, expanded, onToggle, navigate, allGroups, getLocalizedField }) => {
  const { base, variants, firstProduct, image_url } = group;
  const hasVariants = variants.length > 1;
  const handleClick = () => { navigate(`/products/${firstProduct.id}`); };

  return (
    <>
      <div className={`border cursor-pointer transition-all hover:border-[#BE1622]/30 ${expanded ? 'border-[#BE1622] shadow-sm' : 'border-gray-200'}`} data-testid={`product-group-${index}`} onClick={handleClick}>
        <div className="aspect-[4/3] bg-[#F8FAFC] p-4 flex items-center justify-center">
          <img src={image_url || ''} alt={base} className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="p-3 border-t border-gray-100">
          <h3 className="font-semibold text-slate-900 text-xs mb-1 leading-tight">{base}</h3>
          {hasVariants ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {variants.slice(0, 3).map((v, vi) => <span key={vi} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5">{v.label}</span>)}
              {variants.length > 3 && <span className="text-[10px] text-slate-400">+{variants.length - 3}</span>}
            </div>
          ) : firstProduct.specifications?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {firstProduct.specifications.slice(0, 2).map((s, si) => <span key={si} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5">{s.value}</span>)}
            </div>
          )}
          <div className="mt-2 inline-flex items-center text-[10px] text-[#BE1622] font-semibold">{tx.viewDetails} <ChevronRight className="w-3 h-3 ml-0.5" /></div>
        </div>
      </div>
    </>
  );
};

/* Detail Modal */
const DetailModal = ({ group, language, tx, onClose, allGroups, getLocalizedField }) => {
  const { base, variants, image_url, firstProduct } = group;
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [formData, setFormData] = useState({ name: '', company: '', btw: '', email: '', phone: '', country: '', quantity: '1', orderType: 'pallet', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const activeProduct = variants[selectedVariant]?.product || firstProduct;
  const activeSpecs = activeProduct?.specifications || [];
  const recommended = useMemo(() => allGroups.filter(g => g.base !== base && g.category_id === group.category_id).slice(0, 4), [allGroups, base, group.category_id]);

  React.useEffect(() => {
    axios.get(`${API}/certificates/product/${activeProduct?.id}`).then(r => setCertificates(r.data || [])).catch(() => {});
  }, [activeProduct?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, { name: formData.name, email: formData.email, phone: formData.phone, company: formData.company,
        message: `[OFFERTE] ${base}${variants[selectedVariant] ? ' - ' + variants[selectedVariant].label : ''}\nBTW: ${formData.btw}\nLand: ${formData.country}\nBestelvorm: ${formData.orderType}\nAantal: ${formData.quantity}\n\n${formData.message}` });
      setSubmitted(true);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-20 overflow-y-auto" onClick={onClose} data-testid="variant-panel">
      <div className="bg-white max-w-4xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end p-3 pb-0">
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="close-variant-panel"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left */}
          <div className="md:border-r border-gray-100">
            <div className="bg-[#F8FAFC] p-8 flex items-center justify-center"><img src={image_url || ''} alt={base} className="max-w-full max-h-48 object-contain" /></div>
            <div className="p-6">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight mb-4">{base}</h2>
              <div className="mb-4">
                <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.variants}</p>
                <div className="flex flex-wrap gap-1.5">
                  {variants.map((v, vi) => (
                    <button key={vi} onClick={() => setSelectedVariant(vi)} data-testid={`variant-select-${vi}`}
                      className={`px-3 py-1.5 text-xs font-medium border transition-all ${selectedVariant === vi ? 'border-[#BE1622] bg-[#BE1622] text-white' : 'border-gray-200 text-slate-600 hover:border-slate-400'}`}>{v.label}</button>
                  ))}
                </div>
              </div>
              {activeSpecs.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.specs}</p>
                  <div className="border border-gray-200">
                    {activeSpecs.map((spec, si) => (
                      <div key={si} className={`flex text-xs ${si % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                        <span className="w-1/2 px-3 py-2 text-slate-500">{spec[`key_${language}`] || spec.key_nl}</span>
                        <span className="w-1/2 px-3 py-2 text-slate-900 font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Certificates */}
              {certificates.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.certificate}</p>
                  <div className="space-y-1.5">
                    {certificates.map((cert, ci) => (
                      <a key={ci} href={`${process.env.REACT_APP_BACKEND_URL}${cert.file_url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 border border-gray-200 hover:border-[#BE1622]/30 transition-colors text-xs text-slate-700 hover:text-[#BE1622]" data-testid={`cert-download-${ci}`}>
                        <FileDown className="w-4 h-4 flex-shrink-0" />
                        {cert.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Right - Quote Form */}
          <div className="p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4">{tx.requestTitle}</h3>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-10 h-10 bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-3"><Send className="w-4 h-4 text-green-600" /></div>
                <p className="text-sm text-slate-700">{tx.requestSuccess}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="quote-form">
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" required placeholder={tx.requestName} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-name" />
                  <input type="text" placeholder={tx.requestCompany} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-company" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder={tx.requestBtw} value={formData.btw} onChange={(e) => setFormData({ ...formData, btw: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-btw" />
                  <input type="text" placeholder={tx.requestCountry} value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-country" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="email" required placeholder={tx.requestEmail} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-email" />
                  <input type="tel" placeholder={tx.requestPhone} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-phone" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select value={formData.orderType} onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors bg-white" data-testid="quote-order-type">
                    <option value="pallet">{tx.requestOrderPallet}</option>
                    <option value="truck">{tx.requestOrderTruck}</option>
                  </select>
                  <input type="number" min="1" placeholder={tx.requestQty} value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors" data-testid="quote-qty" />
                </div>
                <textarea placeholder={tx.requestMsg} rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] transition-colors resize-none" data-testid="quote-message" />
                <button type="submit" className="w-full bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] py-3 transition-all flex items-center justify-center gap-2" data-testid="quote-submit">
                  <Send className="w-3.5 h-3.5" /> {tx.requestSubmit}
                </button>
              </form>
            )}
          </div>
        </div>
        {/* Recommended */}
        {recommended.length > 0 && (
          <div className="border-t border-gray-100 p-6">
            <h3 className="text-[10px] font-bold text-slate-400 tracking-[0.15em] uppercase mb-3">{tx.recommended}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {recommended.map((rec, ri) => (
                <div key={ri} className="bg-[#F8FAFC] border border-gray-100 p-3 flex flex-col items-center text-center">
                  <img src={rec.image_url || ''} alt={rec.base} className="w-10 h-10 object-contain mb-2" />
                  <p className="text-[10px] font-medium text-slate-600 leading-tight">{rec.base}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
